import { IAssetSummary } from "../api/fetchMaintenance";
import { Asset, AssetManualMetrics, AssetMetrics, AssetModel } from "../models/asset";
import { Scheduling } from "../models/schedule";

export type MetricSource = "asset" | "manual" | "revision";
export type OdometerMetricSource = MetricSource;

export interface EffectiveOdometerMetric {
  odometer: number;
  lastOdometerUpdate?: Date;
  source: MetricSource;
}

export interface EffectiveHourmeterMetric {
  hourmeter: number;
  lastHourmeterUpdate?: Date;
  source: MetricSource;
}

interface MetricCandidate {
  value?: number;
  date?: Date;
  source: MetricSource;
}

const getDateTime = (date?: Date | null): number => {
  if (!date) return 0;
  const time = new Date(date).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const getMostRecentSchedulingWithMetric = (
  previousCheckups: Scheduling[],
  metric: "odometer" | "hourmeter"
): Scheduling | undefined => {
  return previousCheckups
    .filter((scheduling) => (scheduling[metric] ?? 0) > 0)
    .sort((a, b) => getDateTime(b.date) - getDateTime(a.date))[0];
};

const pickMostRecentMetric = (
  primaryValue?: number,
  primaryDate?: Date,
  revisionValue?: number,
  revisionDate?: Date
): { value?: number; date?: Date } => {
  const hasPrimary = (primaryValue ?? 0) > 0;
  const hasRevision = (revisionValue ?? 0) > 0;

  if (!hasPrimary && !hasRevision) {
    return { value: primaryValue, date: primaryDate };
  }
  if (!hasPrimary) {
    return { value: revisionValue, date: revisionDate };
  }
  if (!hasRevision) {
    return { value: primaryValue, date: primaryDate };
  }

  const primaryTime = getDateTime(primaryDate);
  const revisionTime = getDateTime(revisionDate);

  if (primaryTime !== revisionTime) {
    return revisionTime > primaryTime
      ? { value: revisionValue, date: revisionDate }
      : { value: primaryValue, date: primaryDate };
  }

  return (revisionValue ?? 0) >= (primaryValue ?? 0)
    ? { value: revisionValue, date: revisionDate }
    : { value: primaryValue, date: primaryDate };
};

const getRevisionHourmeter = (scheduling?: Scheduling): number | undefined => {
  const value = scheduling?.hourmeter ?? scheduling?.maintenanceData?.hourmeter;
  return (value ?? 0) > 0 ? Number(value) : undefined;
};

const getMetricCandidates = (
  asset: Asset,
  uptimeAsset: AssetManualMetrics | undefined,
  metric: "odometer" | "hourmeter"
): MetricCandidate[] => {
  const previous = asset.schedulings?.previous ?? [];

  if (metric === "odometer") {
    const lastRevision = getMostRecentSchedulingWithMetric(previous, "odometer");

    return [
      { value: asset.odometer, date: asset.lastOdometerUpdate, source: "asset" },
      { value: uptimeAsset?.odometer, date: uptimeAsset?.lastOdometerUpdate, source: "manual" },
      { value: lastRevision?.odometer, date: lastRevision?.date, source: "revision" },
    ];
  }

  const lastRevision = getMostRecentSchedulingWithMetric(previous, "hourmeter");

  return [
    { value: asset.hourmeter, date: asset.lastHourmeterUpdate, source: "asset" },
    { value: uptimeAsset?.hourmeter, date: uptimeAsset?.lastHourmeterUpdate, source: "manual" },
    { value: getRevisionHourmeter(lastRevision), date: lastRevision?.date, source: "revision" },
  ];
};

const pickEffectiveMetricByDate = (
  candidates: MetricCandidate[]
): MetricCandidate | null => {
  const validCandidates = candidates.filter((candidate) => (candidate.value ?? 0) > 0);

  if (validCandidates.length === 0) return null;

  return validCandidates.reduce((best, current) => {
    const bestTime = getDateTime(best.date);
    const currentTime = getDateTime(current.date);

    if (currentTime !== bestTime) {
      return currentTime > bestTime ? current : best;
    }

    return (current.value ?? 0) >= (best.value ?? 0) ? current : best;
  });
};

export const getEffectiveOdometer = (
  asset: Asset,
  uptimeAsset?: AssetManualMetrics
): EffectiveOdometerMetric | null => {
  const winner = pickEffectiveMetricByDate(getMetricCandidates(asset, uptimeAsset, "odometer"));

  if (!winner) return null;

  return {
    odometer: winner.value!,
    lastOdometerUpdate: winner.date,
    source: winner.source,
  };
};

export const getEffectiveHourmeter = (
  asset: Asset,
  uptimeAsset?: AssetManualMetrics
): EffectiveHourmeterMetric | null => {
  const winner = pickEffectiveMetricByDate(getMetricCandidates(asset, uptimeAsset, "hourmeter"));

  if (!winner) return null;

  return {
    hourmeter: winner.value!,
    lastHourmeterUpdate: winner.date,
    source: winner.source,
  };
};

export const getAssetIdByChassis = (assets: any[], chassis: string): string | undefined => {
  return assets
    .find((asset) => asset.chassis === chassis)?.id;
}

export const getAssetIdsByChassisList = (assets: any[], chassisList: string[]): string[] => {
  const chassisSet = new Set(chassisList);
  return assets
    .filter((asset) => chassisSet.has(asset.chassis))
    .map((asset) => asset.id);
}

export const getAssetIdsByChassisListFromAssetSummary = (assets: IAssetSummary[], chassisList: string[]): string[] => {
  const chassisSet = new Set(chassisList);
  return assets
    .filter((asset) => chassisSet.has(asset.identification))
    .map((asset) => asset.id);
}

export const getChassisListFromAssetIds = (allAssets: Asset[], assetIds: string[]): string[] => {
  if(allAssets.length === assetIds.length) return [];
  
  return allAssets
    .filter((asset) => assetIds.includes(asset.id))
    .map((asset) => asset.chassis);
}

export const getChassisFromAssetName = (allAssets: Asset[], assetNames: string[]): string[] => {
  return allAssets
    .filter((asset) => assetNames.includes(asset.name))
    .map((asset) => asset.chassis);
}

export const getLastAssetMetrics = (asset: Asset, previousCheckups: Scheduling[]): AssetMetrics => {
  const lastOdometerRevision = getMostRecentSchedulingWithMetric(previousCheckups, "odometer");
  const lastHourmeterRevision = getMostRecentSchedulingWithMetric(previousCheckups, "hourmeter");

  const odometerResult = pickMostRecentMetric(
    asset.odometer,
    asset.lastOdometerUpdate,
    lastOdometerRevision?.odometer,
    lastOdometerRevision?.date
  );
  const hourmeterResult = pickMostRecentMetric(
    asset.hourmeter,
    asset.lastHourmeterUpdate,
    getRevisionHourmeter(lastHourmeterRevision),
    lastHourmeterRevision?.date
  );

  return {
    assetId: asset.id,
    odometer: odometerResult.value,
    lastOdometerUpdate: odometerResult.date,
    hourmeter: hourmeterResult.value,
    lastHourmeterUpdate: hourmeterResult.date,
  };
};
export const getAssetModelToSelectOption = (id?: string, assetModels?: AssetModel[]) => {
  if(!id) return null;

  // Check if id is a UUID
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (uuidRegex.test(id)) {
    return assetModels?.find((model) => model.modelId === id) || null;
  }

  return { modelCode: id, description: id };
};

export const getAssetByChassis = (allAssets: Asset[], chassis: string): Asset | undefined => {
  return allAssets.find((asset) => asset.chassis === chassis);
}
